import { StaffMember, StaffTreeModule } from './types';

/**
 * Чистые функции раздела «Сотрудники точки». Проверки формы повторяют серверные
 * (staffAccounts.tailError, profileView.passwordError), чтобы ошибка была видна до отправки; решает сервер.
 */

const TAIL_PATTERN = /^[a-z0-9][a-z0-9_-]{1,29}$/;
const MIN_PASSWORD_LENGTH = 6;
const MAX_PASSWORD_LENGTH = 128;
const CYRILLIC = /[а-яё]/i;
export const MIN_NAME_LENGTH = 2;
export const MAX_NAME_LENGTH = 150;

/** Окончание логина вводится в нижнем регистре и без пробелов */
export const normalizeTail = (value: string): string => value.toLowerCase().replace(/\s+/g, '');

export function tailProblem(tail: string): string | null {
  if (!tail) return null;
  return TAIL_PATTERN.test(tail)
    ? null
    : 'От 2 до 30 символов: латиница, цифры, «_» и «-», начинается с буквы или цифры';
}

/** Пустой пароль допустим: его сгенерирует сервер */
export function passwordProblem(password: string, login: string): string | null {
  if (!password) return null;
  if (password.length < MIN_PASSWORD_LENGTH) return `Пароль должен содержать не менее ${MIN_PASSWORD_LENGTH} символов`;
  if (password.length > MAX_PASSWORD_LENGTH) return `Пароль - не длиннее ${MAX_PASSWORD_LENGTH} символов`;
  if (password.trim() !== password) return 'Пароль не должен начинаться или заканчиваться пробелом';
  if (CYRILLIC.test(password)) return 'Пароль не должен содержать русские буквы: проверьте раскладку клавиатуры';
  if (login && password.toLowerCase() === login.toLowerCase()) return 'Пароль не должен совпадать с логином';
  return null;
}

export const nameLength = (value: string): number => value.trim().replace(/\s+/g, ' ').length;

export function filterStaff(rows: StaffMember[], query: string): StaffMember[] {
  const q = query.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter(row => row.fullName.toLowerCase().indexOf(q) >= 0 || row.login.toLowerCase().indexOf(q) >= 0);
}

/** Коды, которые можно выдать: меню и права разделов дерева точки */
function grantableCodes(tree: StaffTreeModule[]): Record<string, string> {
  const menuOf: Record<string, string> = {};
  tree.forEach((module) => {
    menuOf[module.menu] = module.menu;
    module.items.forEach((item) => { menuOf[item.code] = module.menu; });
  });
  return menuOf;
}

/**
 * Галочки в виде, который примет сервер: только коды дерева точки, права раздела - только при его меню.
 * Коды вне дерева (раздел снят с роли точки) сервер хранит сам и в форме не показываются.
 */
export function normalizeSelection(codes: string[], tree: StaffTreeModule[]): string[] {
  const menuOf = grantableCodes(tree);
  const picked: Record<string, boolean> = {};
  codes.forEach((code) => { picked[code] = true; });
  return Object.keys(picked)
    .filter(code => Boolean(menuOf[code]) && picked[menuOf[code]])
    .sort();
}

export function sameCodes(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((code, i) => code === b[i]);
}

/** Раздел целиком: включение выдаёт вкладку и все её права, выключение снимает всё */
export function toggleModule(codes: string[], module: StaffTreeModule, on: boolean): string[] {
  const own = [module.menu].concat(module.items.map(item => item.code));
  const rest = codes.filter(code => own.indexOf(code) < 0);
  return on ? rest.concat(own) : rest;
}

export function toggleCode(codes: string[], code: string, on: boolean): string[] {
  const rest = codes.filter(c => c !== code);
  return on ? rest.concat([code]) : rest;
}

/** Все разделы дерева со всеми правами */
export const allCodes = (tree: StaffTreeModule[]): string[] => Object.keys(grantableCodes(tree)).sort();

/** Сколько разделов (вкладок) выдано сотруднику */
export function grantedModules(codes: string[], tree: StaffTreeModule[]): number {
  return tree.filter(module => codes.indexOf(module.menu) >= 0).length;
}

/** 'YYYY-MM-DD HH:mm:ss' с сервера - в «дд.мм.гггг» и «чч:мм» */
export function splitMoment(value: string | null): { date: string; time: string } {
  const match = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(value || '');
  if (!match) return { date: value || '-', time: '' };
  return { date: `${match[3]}.${match[2]}.${match[1]}`, time: `${match[4]}:${match[5]}` };
}

/** Метка времени сессии (ISO) - в местное время */
export function formatInstant(value: string | null | undefined): string {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('ru-RU');
}

export const limitReached = (active: number, limit: number): boolean => limit > 0 && active >= limit;

export async function copyText(value: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(value);
      return true;
    }
    // Стенд по http: Clipboard API недоступен, остаётся выделение текста во временном поле
    const area = document.createElement('textarea');
    area.value = value;
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const done = document.execCommand('copy');
    document.body.removeChild(area);
    return done;
  } catch {
    return false;
  }
}
